/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { BACK_CLICK_WINDOW_MS, INVALID_CLICK_WINDOW_MS } from '../activity/constants';
import { EventDispatcher, EventObject } from '../events';
import Library from '../library';
import { Logger } from '../logger';
import { GlobalFactStore } from '../rules/facts/store';

import type { Facts } from '../rules';
import type {
  BaseEventAnalyticsData,
  GenericEventAnalyticsData,
  ImpressionAnalyticsData,
  KeyValue,
} from '../types';
import type { XdValue } from '../types/collection';
import type { UserConsent } from '../types/prebid';
import { debugPixel, encodeJsonBase64, xorEncode } from '../util';
import type { ReportingEvents } from './events';

export interface AnalyticsConfig {
  enabled?: boolean;
  endpoint?: {
    origin: string;
    /**
     * Origin for observational/training pixel traffic (`t=1`). Defaults to
     * `https://t.nodals.io`; falls back to `origin` when unset.
     */
    trainingOrigin?: string;
  };
}

export interface AnalyticsOptions {
  config: AnalyticsConfig;
  propertyId: string;
}

const enum RequestType {
  GET = 'GET',
  POST = 'POST',
}

const KEY = 'NDLS';
const BASE_PATH = '/d/v1';
const IMPRESSION_ENDPOINT = '/imp';
const AD_INTERACTION_ENDPOINT = '/int';
const ACCIDENTAL_CLICK_ENDPOINT = '/aclk';
const INVALID_CLICK_ENDPOINT = '/iclk';
const VIEWABLE_IMP_ENDPOINT = '/vi';
const FILTERED_SELECTION_REPORT_ENDPOINT = '/ckv';
const PAGE_VIEW_ENDPOINT = '/pv';
const VIDEO_QUARTILE_ENDPOINT = '/vq';
const BRAND_SAFETY_VIOLATION_ENDPOINT = '/bv';
const logger = Logger.getInstance();

enum EventType {
  IMPRESSION = 'impression',
  AD_INTERACTION = 'interaction',
  ACCIDENTAL_CLICK = 'accidentalClick',
  INVALID_CLICK = 'invalidClick',
  VIEWABLE_IMPRESSION = 'viewableImpression',
  FILTERED_SELECTION_REPORT = 'filteredSelectionReport',
  PAGE_VIEW = 'pageView',
  VIDEO_QUARTILE = 'videoQuartile',
  BRAND_SAFETY_VIOLATION = 'brandSafetyViolation',
}

class AnalyticsRecorder {
  private config: AnalyticsConfig;
  private enabled: boolean = true;
  private propertyId: string;
  private eventDispatcher: EventDispatcher<ReportingEvents>;
  private baseUrl: string;
  private trainingBaseUrl: string;

  constructor(eventDispatcher: EventDispatcher<ReportingEvents>, options: AnalyticsOptions) {
    this.eventDispatcher = eventDispatcher;
    this.config = options.config;
    this.propertyId = options.propertyId;

    // Both base URLs are resolved once here so event handling never rebuilds
    // them - at 50 placements each firing imp/vi/int this is a hot path.
    // `endpoint` is optional and is legitimately absent when analytics is
    // disabled, in which case no request is ever made from these values.
    const endpoint = options.config.endpoint;
    this.baseUrl = `${endpoint?.origin ?? ''}${BASE_PATH}`;
    // Publishers overriding `origin` without a `trainingOrigin` (first-party
    // proxy, test host) keep all traffic on their own host: a derived `t.`
    // subdomain would almost certainly not resolve, and pixels are
    // fire-and-forget, so the loss would be silent.
    this.trainingBaseUrl = `${endpoint?.trainingOrigin ?? endpoint?.origin ?? ''}${BASE_PATH}`;

    if (this.config.enabled !== undefined) {
      this.enabled = Boolean(this.config.enabled);
    }

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    if (!this.enabled) {
      logger.warn('Analytics recording is disabled - no event listeners will be registered');
      return;
    }

    this.eventDispatcher.on('AdImpression', (event) => this.handleAdImpression(event));
    this.eventDispatcher.on('AdViewableImpression', (event) =>
      this.handleAdViewableImpression(event),
    );
    this.eventDispatcher.on('AdInteraction', (event) => this.handleAdInteraction(event));
    this.eventDispatcher.on('AdvertorialInteraction', (event) => this.handleAdInteraction(event));
    this.eventDispatcher.on('AccidentalClick', (event) => this.handleAccidentalClick(event));
    this.eventDispatcher.on('AdvertorialImpression', (event) =>
      this.handleAdvertorialImpression(event),
    );
    this.eventDispatcher.on('TargetingSelectionComplete', (event) =>
      this.handleTargetingSelectionComplete(event),
    );
    this.eventDispatcher.on('PageView', (event) => this.handlePageView(event));
    this.eventDispatcher.on('VideoQuartile', (event) => this.handleVideoQuartile(event));
    this.eventDispatcher.on('BrandSafetyViolation', (event) =>
      this.handleBrandSafetyViolation(event),
    );
  }

  /**
   * Observational events (`t=1` - learning, monitoring, advertorial and
   * programmatic-served deliveries) are routed to the training origin so that
   * training traffic can be scaled and ingested separately from campaign
   * traffic. Callers without a `t` (page view, filtered selection) default to
   * the campaign origin.
   */
  private getEndpointUrl(eventType: EventType, isTraining: boolean = false): string {
    const baseUrl = isTraining ? this.trainingBaseUrl : this.baseUrl;
    switch (eventType) {
      case EventType.IMPRESSION:
        return `${baseUrl}${IMPRESSION_ENDPOINT}`;
      case EventType.AD_INTERACTION:
        return `${baseUrl}${AD_INTERACTION_ENDPOINT}`;
      case EventType.ACCIDENTAL_CLICK:
        return `${baseUrl}${ACCIDENTAL_CLICK_ENDPOINT}`;
      case EventType.INVALID_CLICK:
        return `${baseUrl}${INVALID_CLICK_ENDPOINT}`;
      case EventType.VIEWABLE_IMPRESSION:
        return `${baseUrl}${VIEWABLE_IMP_ENDPOINT}`;
      case EventType.FILTERED_SELECTION_REPORT:
        return `${baseUrl}${FILTERED_SELECTION_REPORT_ENDPOINT}`;
      case EventType.PAGE_VIEW:
        return `${baseUrl}${PAGE_VIEW_ENDPOINT}`;
      case EventType.VIDEO_QUARTILE:
        return `${baseUrl}${VIDEO_QUARTILE_ENDPOINT}`;
      case EventType.BRAND_SAFETY_VIOLATION:
        return `${baseUrl}${BRAND_SAFETY_VIOLATION_ENDPOINT}`;
    }
  }

  private getInitialQueryParamsMap(
    userConsent: UserConsent,
    requestType: RequestType,
  ): Record<string, string> {
    let params: Record<string, string> = {
      ...this.generateConsentParamsMap(userConsent),
      rnd: String(Math.random()),
    };
    if (requestType === RequestType.GET) {
      params.dl = `${Library.name}:${Library.version}`;
    }
    return params;
  }

  private handleAdImpression(event: EventObject<ReportingEvents['AdImpression']>): void {
    const { userConsent, ...data } = event.data;

    logger.info(
      `Logging impression for opportunity: ${data.opportunityId} ;impression: ${data.id}`,
    );
    const endpointUrl: string = this.getEndpointUrl(EventType.IMPRESSION, data.t === 1);
    const parameterMap = this.buildImpressionParameters({ ...data, userConsent });

    const querystring: string = new URLSearchParams(
      Object.entries(parameterMap).filter(([_, value]) => value !== null && value !== undefined),
    ).toString();

    this.makeGetRequest(`${endpointUrl}?${querystring}`);
  }

  private handleAdViewableImpression(
    event: EventObject<ReportingEvents['AdViewableImpression']>,
  ): void {
    this.handleGenericEvent(
      event.data,
      EventType.VIEWABLE_IMPRESSION,
      `Logging viewable impression for opportunity: ${event.data.opportunityId} ;impression: ${event.data.impressionId}`,
      (data) => this.buildGenericEventParameters(data),
      event.data.useHttpPost ? 'POST' : 'GET',
    );
  }

  private handleAdInteraction(event: EventObject<ReportingEvents['AdInteraction']>): void {
    this.handleGenericEvent(
      event.data,
      EventType.AD_INTERACTION,
      `Logging interaction for opportunity: ${event.data.opportunityId} ;impression: ${event.data.impressionId}`,
      (data) => this.buildGenericEventParameters(data),
      event.data.useHttpPost ? 'POST' : 'GET',
    );
  }

  private handleAccidentalClick(event: EventObject<ReportingEvents['AccidentalClick']>): void {
    // Recorded via an image-pixel HTTP GET. Accidental clicks are detected while
    // the page is alive and in the foreground (re-engagement signal), so there is
    // no in-flight navigation to lose the request to — a beacon is unnecessary.
    // The impression id rides under the same key as the interaction endpoint via
    // buildGenericEventParameters. awayDuration (ms since the interaction was
    // counted) is sent as 'd'.
    //
    // A single detected bad click can drive two pixels, decided here from the one
    // awayDuration so the thresholds live in one place:
    //   - /iclk (analytics-only) when the return is within INVALID_CLICK_WINDOW_MS (1h)
    //   - /aclk (the reported signal) when the return is within BACK_CLICK_WINDOW_MS (3s)
    // So a <=3s return fires both; a 3s–1h return fires only /iclk.
    const { awayDuration } = event.data;

    if (awayDuration >= 0 && awayDuration <= INVALID_CLICK_WINDOW_MS) {
      this.handleGenericEvent(
        event.data,
        EventType.INVALID_CLICK,
        `Logging invalid click for opportunity: ${event.data.opportunityId} ;impression: ${event.data.impressionId}`,
        (data) => this.buildGenericEventParameters(data, { d: String(data.awayDuration) }),
        'GET',
      );
    }

    if (awayDuration >= 0 && awayDuration <= BACK_CLICK_WINDOW_MS) {
      this.handleGenericEvent(
        event.data,
        EventType.ACCIDENTAL_CLICK,
        `Logging accidental click for opportunity: ${event.data.opportunityId} ;impression: ${event.data.impressionId}`,
        (data) => this.buildGenericEventParameters(data, { d: String(data.awayDuration) }),
        'GET',
      );
    }
  }

  private handleAdvertorialImpression(
    event: EventObject<ReportingEvents['AdvertorialImpression']>,
  ): void {
    const { opportunityId, adUnit, id, deliveryId, meta, pvid, sid, uid, userConsent, xd } =
      event.data;

    logger.info(
      `Logging advertorial impression for opportunity: ${opportunityId}, placement: ${adUnit}, delivery: ${deliveryId}`,
    );

    const endpointUrl = this.getEndpointUrl(EventType.IMPRESSION, event.data.t === 1);
    const parameterMap: Record<string, string> = this.getInitialQueryParamsMap(
      userConsent,
      RequestType.GET,
    );

    Object.assign(parameterMap, {
      rid: opportunityId,
      id,
      did: deliveryId,
      gad: adUnit,
      pid: this.propertyId,
      pvid,
      sid,
      t: String(event.data.t),
      plcmt_v: String(event.data.v),
      plcmt_t: String(event.data.pt),
      ...(uid ? { uid } : {}),
      ...(meta?.clkurl || meta?.click_url ? { clkurl: meta.clkurl || meta.click_url } : {}),
      ...(xd && Object.keys(xd).length > 0 ? { xd: encodeJsonBase64(xd) } : {}),
    });

    if (meta && Object.keys(meta).length > 0) {
      // Serialize meta as URLSearchParams, XOR encode with 'NDLS', and add as 'm' parameter
      const metaQueryString = new URLSearchParams(meta).toString();
      const encodedMeta = xorEncode(metaQueryString, KEY);
      parameterMap.m = encodedMeta;
    }

    const querystring = new URLSearchParams(
      Object.entries(parameterMap).filter(([_, value]) => value !== null && value !== undefined),
    ).toString();

    this.makeGetRequest(`${endpointUrl}?${querystring}`);
  }

  private handleTargetingSelectionComplete(
    event: EventObject<ReportingEvents['TargetingSelectionComplete']>,
  ): void {
    const { opportunityId, selectionData, userConsent, selectionLabel } = event.data;

    // Record filtered selection data
    logger.info(`Logging filtered selection reporting for opportunity: ${opportunityId}`);
    const selectionEndpointUrl: string = this.getEndpointUrl(EventType.FILTERED_SELECTION_REPORT);
    const parameterMap: Record<string, string> = this.getInitialQueryParamsMap(
      userConsent,
      RequestType.POST,
    );
    const selectionPayload: Record<string, any> = {
      id: opportunityId,
      plcmts: selectionData,
      dl: `${Library.name}:${Library.version}`,
      l: selectionLabel,
    };

    const querystring: string = new URLSearchParams(parameterMap).toString();
    this.makePostRequest(`${selectionEndpointUrl}?${querystring}`, selectionPayload);
  }

  private handlePageView(event: EventObject<ReportingEvents['PageView']>): void {
    const { id, sid, uid, userConsent, xd, pcids } = event.data;

    // Record page view data
    logger.info(`Logging page view: ${id}`);
    const pageViewEndpointUrl: string = this.getEndpointUrl(EventType.PAGE_VIEW);
    const parameterMap: Record<string, string> = this.getInitialQueryParamsMap(
      userConsent,
      RequestType.POST,
    );
    const pageViewPayload: Record<string, any> = this.generatePageViewPayload(
      id,
      sid,
      uid,
      xd,
      pcids,
    );

    const querystring: string = new URLSearchParams(parameterMap).toString();
    this.makePostRequest(`${pageViewEndpointUrl}?${querystring}`, pageViewPayload);
  }

  private handleVideoQuartile(event: EventObject<ReportingEvents['VideoQuartile']>): void {
    const { quartile } = event.data;
    this.handleGenericEvent(
      event.data,
      EventType.VIDEO_QUARTILE,
      `Logging video quartile ${quartile} for opportunity: ${event.data.opportunityId} ;impression: ${event.data.impressionId}`,
      (data) => this.buildGenericEventParameters(data, { q: String(quartile) }),
      event.data.useHttpPost ? 'POST' : 'GET',
    );
  }

  private handleBrandSafetyViolation(
    event: EventObject<ReportingEvents['BrandSafetyViolation']>,
  ): void {
    const {
      cid,
      pvid,
      sid,
      adUnit,
      detectedWords,
      //@ts-ignore
      userConsent,
      //@ts-ignore
      uid,
      impressionId,
      bidStage,
      opportunityId,
    } = event.data;

    logger.info(`Logging brand safety violation for campaign ${cid} on adUnit ${adUnit}`);

    const payload = {
      en: 'bsv',
      cid: cid,
      pvid: pvid,
      sid: sid,
      //uid: uid || '',
      gad: adUnit,
      dwds: this.base64Encode(detectedWords.sort().join(',')),
      id: impressionId || '',
      bidStage: bidStage,
      rid: opportunityId,
    };

    // Temporary solution: use debugPixel until backend is ready
    debugPixel(payload, true);

    // TODO: Uncomment when backend is ready
    // const endpointUrl: string = this.getEndpointUrl(EventType.BRAND_SAFETY_VIOLATION);
    // const queryParams = this.getInitialQueryParamsMap(userConsent, RequestType.GET);
    // const querystring: string = new URLSearchParams(queryParams).toString();
    // this.makePostRequest(`${endpointUrl}?${querystring}`, payload);
  }

  private serializeKeyValues(keyValues: KeyValue[]): string {
    return keyValues.map((kv) => `${kv.key}=${kv.value}`).join(':');
  }

  private base64Encode(input: string): string {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(input);
      const base64 = btoa(String.fromCharCode(...data));
      // Make it URL-safe by replacing characters
      return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    } catch (error) {
      // Fallback to btoa for compatibility
      return btoa(input).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    }
  }

  private generateConsentParamsMap(userConsent: UserConsent): Record<string, string> {
    return {
      gdpr_consent: userConsent?.gdpr?.consentString ?? '',
      gdpr: userConsent?.gdpr?.gdprApplies ? '1' : '0',
      us_privacy: userConsent?.uspConsent ?? '',
      gpp: userConsent?.gpp?.gppString ?? '',
      gpp_sid:
        userConsent.gpp && Array.isArray(userConsent.gpp.applicableSections)
          ? userConsent.gpp.applicableSections.join(',')
          : '',
    };
  }

  private makePostRequest(url: string, payload?: any): void {
    const payloadString = payload ? JSON.stringify(payload) : undefined;
    logger.info(`Making HTTP POST analytics request to ${url}`);

    const success = navigator.sendBeacon(url, payloadString);

    if (!success) {
      try {
        fetch(url, {
          method: 'POST',
          body: payloadString,
          keepalive: false,
          headers: {
            'Content-Type': 'application/json',
          },
        }).catch((error) => {
          this.trackSendBeaconFailure(url, String(error));
        });
      } catch (error) {
        this.trackSendBeaconFailure(url, String(error));
      }
    }
  }

  private trackSendBeaconFailure(url: string, error: string): void {
    try {
      debugPixel(
        {
          en: 'sendBeaconFailure',
          url: url.substring(
            url.lastIndexOf('/'),
            url.indexOf('?') > 0 ? url.indexOf('?') : url.length,
          ),
          error: error.substring(0, 100),
        },
        true,
      );
    } catch (e) {}
  }

  private makeGetRequest(url: string, payload: any = undefined): void {
    let urlWithOptPayload: string = url;
    if (payload) {
      urlWithOptPayload = `${url}&data=${encodeJsonBase64(payload)}`;
    }
    logger.info(`Making HTTP GET analytics request to ${urlWithOptPayload}`);
    new Image().src = urlWithOptPayload;
  }

  private generateCommonPayloadProperties(uid?: string): Record<string, any> {
    const globalFacts: Facts = GlobalFactStore.getInstance().getFacts();
    const payload: any = {
      pid: this.propertyId,
      ref: globalFacts['page.url'] ?? '',
      pref: globalFacts['page.referrer'] ?? '',
      br: globalFacts['browser.name'] ?? '',
      bl: globalFacts['browser.lang'] ?? '',
      bw: globalFacts['browser.width'] ?? 0,
      bh: globalFacts['browser.height'] ?? 0,
      dt: globalFacts['device.type'] ?? '',
      os: globalFacts['device.os'] ?? '',
      to: globalFacts['time.offset'],
      cy: globalFacts['geo.city'] ?? '',
      ct: globalFacts['geo.country'] ?? '',
      pc: globalFacts['geo.postcode'] ?? '',
      dl: `${Library.name}:${Library.version}`,
    };
    const publisherCohorts: String[] = globalFacts['publisher.cohort.ids'];
    if (publisherCohorts && publisherCohorts.length > 0) {
      payload.pcids = publisherCohorts;
    }
    if (uid) {
      payload.uid = uid;
    }
    return payload;
  }

  private generatePageViewPayload(
    id: string,
    sid: string,
    uid?: string,
    xd?: Record<string, XdValue>,
    pcids?: string[],
  ): Record<string, any> {
    const payload: any = {
      ...this.generateCommonPayloadProperties(uid),
      id: id,
      sid: sid,
    };
    if (xd && Object.keys(xd).length > 0) {
      // The page-view wire contract carries xd as a JSON-encoded string.
      payload.xd = JSON.stringify(xd);
    }
    // A dcfg-collected pcids array overrides the vendor-derived publisher
    // cohorts set by generateCommonPayloadProperties.
    if (pcids && pcids.length > 0) {
      payload.pcids = pcids;
    }
    return payload;
  }

  private buildBaseEventParameters(
    data: BaseEventAnalyticsData,
    userConsent: UserConsent,
    additionalParams: Record<string, any> = {},
  ): Record<string, string> {
    const parameterMap: Record<string, string> = this.getInitialQueryParamsMap(
      userConsent,
      RequestType.GET,
    );

    Object.assign(parameterMap, {
      rid: data.opportunityId,
      did: data.deliveryId,
      eai: data.advertiserId,
      eadms: data.advertiserDomains,
      gad: data.adUnit,
      lid: data.lineItemId,
      dlid: data.nodalsDealId,
      pvid: data.pvid,
      sid: data.sid,
      uid: data.uid,
      pid: this.propertyId,
      l: data.selectionLabel,
      ...additionalParams,
    });

    return parameterMap;
  }

  private buildGenericEventParameters(
    data: GenericEventAnalyticsData,
    additionalParams: Record<string, any> = {},
  ): Record<string, string> {
    const params = this.buildBaseEventParameters(data, data.userConsent, {
      id: data.impressionId,
      ...additionalParams,
    });

    // Only emit `t` when present. Reduced-data callers (e.g. the redirect
    // accidental-click reconcile, which has no campaign context) may omit it;
    // never stringify undefined into a literal "undefined" on the wire.
    if (data.t != null) {
      params.t = String(data.t);
    }

    return params;
  }

  private buildImpressionParameters(
    data: ImpressionAnalyticsData & { userConsent: UserConsent },
  ): Record<string, string> {
    const parameterMap = this.buildBaseEventParameters(data, data.userConsent, {
      id: data.id,
      crw: data.renderedData?.size?.width,
      crh: data.renderedData?.size?.height,
      crid: data.creativeId,
      vd: data.durationSeconds,
    });

    parameterMap.t = String(data.t);
    parameterMap.plcmt_v = String(data.v);
    parameterMap.plcmt_t = String(data.pt);

    if (data.kvs && data.kvs.length > 0) {
      parameterMap.kvs = this.serializeKeyValues(data.kvs);
    }

    // The impression wire contract carries xd as base64-encoded JSON, passed
    // through the pixel server unmodified and decoded downstream.
    if (data.xd) {
      parameterMap.xd = encodeJsonBase64(data.xd);
    }

    return parameterMap;
  }

  private handleGenericEvent<T extends { userConsent: UserConsent; t?: number }>(
    eventData: T,
    eventType: EventType,
    logMessage: string,
    parameterBuilder: (data: T) => Record<string, string>,
    requestMethod: 'GET' | 'POST' = 'GET',
  ): void {
    logger.info(logMessage);
    const endpointUrl: string = this.getEndpointUrl(eventType, eventData.t === 1);
    const parameterMap = parameterBuilder(eventData);

    const querystring: string = new URLSearchParams(
      Object.entries(parameterMap).filter(([_, value]) => value != null),
    ).toString();

    if (requestMethod === 'POST') {
      this.makePostRequest(`${endpointUrl}?${querystring}`);
    } else {
      this.makeGetRequest(`${endpointUrl}?${querystring}`);
    }
  }
}

export type { ReportingEvents } from './events';
export { AnalyticsRecorder };
